// client/src/components/admin/AdminRequestDetails.js
// Enhanced version with better completed request info and currency support
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const AdminRequestDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [bids, setBids] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionInProgress, setActionInProgress] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchRequestDetails = async () => {
      try {
        // Fetch request details
        const res = await axios.get(`/api/requests/${id}`);
        if (res.data && res.data.request) {
          setRequest(res.data.request);
          setBids(res.data.bids || []);
          
          // Fetch allocations - try multiple possible paths in the API response
          let requestAllocations = [];
          
          // Check if allocations exist directly in the request object
          if (Array.isArray(res.data.request.allocations)) {
            requestAllocations = res.data.request.allocations;
          } 
          // Check if allocations are at the top level of the response
          else if (Array.isArray(res.data.allocations)) {
            requestAllocations = res.data.allocations;
          }
          // As a fallback, try to get allocations from a separate API endpoint
          else if (res.data.request.status === 'pending_transfer' || res.data.request.status === 'completed') {
            try {
              const allocRes = await axios.get(`/api/allocations/request/${id}`);
              if (allocRes.data && Array.isArray(allocRes.data.allocations)) {
                requestAllocations = allocRes.data.allocations;
              }
            } catch (allocErr) {
              console.warn('Could not fetch allocations from separate endpoint:', allocErr);
              // Non-critical error, continue with empty allocations
              requestAllocations = [];
            }
          }
          
          setAllocations(requestAllocations);
          
          // If it's a completed request, fetch related transactions
          if (res.data.request.status === 'completed' || res.data.request.status === 'pending_transfer') {
            try {
              const transRes = await axios.get(`/api/transactions/request/${id}`);
              if (transRes.data && transRes.data.transactions) {
                setTransactions(transRes.data.transactions);
              }
            } catch (transErr) {
              console.warn('Could not fetch transactions:', transErr);
              // Non-critical error, don't block the UI
            }
          }
        } else {
          setError('Invalid response format from server');
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching request:', err);
        setError('Failed to fetch request details');
        setLoading(false);
      }
    };
    
    fetchRequestDetails();
  }, [id]);

  const handleStatusUpdate = async (newStatus) => {
    setActionInProgress(true);
    setError('');
    setSuccessMessage('');
    
    try {
      await axios.put(`/api/requests/${id}/status`, { status: newStatus });
      
      // Update the request in state
      setRequest({ ...request, status: newStatus });
      setSuccessMessage(`Request status updated to ${newStatus.replace('_', ' ')}`);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
      // If the status changed, we should refresh the data to get the latest allocations
      if (newStatus === 'pending_transfer' || newStatus === 'completed') {
        // Fetch fresh data
        const res = await axios.get(`/api/requests/${id}`);
        if (res.data && res.data.request) {
          setRequest(res.data.request);
          setBids(res.data.bids || []);
          setAllocations(res.data.request.allocations || []);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update request status');
    } finally {
      setActionInProgress(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDeadline = (dateString) => {
    const deadline = new Date(dateString);
    const now = new Date();
    
    // Check if deadline has passed
    const isPast = deadline < now;
    
    return (
      <span className={isPast ? 'admin-deadline-passed' : 'admin-deadline-active'}>
        {formatDate(dateString)}
        {isPast && ' (Expired)'}
      </span>
    );
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    if (amount === undefined || amount === null) return 'N/A';
    return `${currency} ${(Number(amount) || 0).toLocaleString('en-US')}`;
  };

  // Get winning bids (approved bids)
  const getWinningBids = () => {
    return bids.filter(bid => bid.status === 'approved');
  };

  // Calculate total allocated amount from available data sources
  const getTotalAllocated = () => {
    // First check if request has a totalAllocated property (in case the API provides it directly)
    if (request && typeof request.totalAllocated === 'number') {
      return request.totalAllocated;
    }
    
    // Next try to calculate from allocations
    if (allocations && allocations.length > 0) {
      const total = allocations.reduce((sum, allocation) => {
        // Ensure we're dealing with a number by explicitly converting
        // Handle both numeric and string representations
        let amount = 0;
        if (typeof allocation.amount === 'number') {
          amount = allocation.amount;
        } else if (typeof allocation.amount === 'string') {
          amount = parseFloat(allocation.amount) || 0;
        }
        return sum + amount;
      }, 0);
      
      return total;
    }
    
    // Fallback: calculate from winning bids' allocatedAmount if available
    const winningBids = getWinningBids();
    if (winningBids.length > 0) {
      // First check if bids have allocatedAmount
      const bidsWithAllocations = winningBids.filter(bid => 
        bid.allocatedAmount !== undefined && bid.allocatedAmount !== null
      );
      
      if (bidsWithAllocations.length > 0) {
        return bidsWithAllocations.reduce((sum, bid) => {
          let amount = 0;
          if (typeof bid.allocatedAmount === 'number') {
            amount = bid.allocatedAmount;
          } else if (typeof bid.allocatedAmount === 'string') {
            amount = parseFloat(bid.allocatedAmount) || 0;
          }
          return sum + amount;
        }, 0);
      }
      
      // If no allocatedAmount, use the full bid amounts as an estimate
      return winningBids.reduce((sum, bid) => {
        let amount = 0;
        if (typeof bid.amount === 'number') {
          amount = bid.amount;
        } else if (typeof bid.amount === 'string') {
          amount = parseFloat(bid.amount) || 0;
        }
        return sum + amount;
      }, 0);
    }
    
    return 0;
  };

  if (loading) {
    return <div className="loading">Loading request details...</div>;
  }

  if (error && !request) {
    return <div className="error-message">{error}</div>;
  }

  if (!request) {
    return <div className="error-message">Request not found</div>;
  }

  const winningBids = getWinningBids();
  const totalAllocated = getTotalAllocated();
  const requestCurrency = request.currency || 'EUR';

  return (
    <div className="admin-container">
      <div className="admin-header">
        <button 
          onClick={() => navigate(-1)} 
          className="btn btn-light"
        >
          &larr; Back
        </button>
        <h2 className="admin-page-title">Request Details #{request.requestId}</h2>
      </div>
      
      {error && <div className="alert-danger">{error}</div>}
      {successMessage && <div className="alert-success">{successMessage}</div>}
      
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Request Information</h3>
          <span className={`status status-${request.status}`}>
            {request.status.replace('_', ' ')}
          </span>
        </div>
        <div className="admin-card-body">
          <div className="admin-detail-row">
            <div className="admin-detail-label">Request ID</div>
            <div className="admin-detail-value">#{request.requestId}</div>
          </div>
          
          <div className="admin-detail-row">
            <div className="admin-detail-label">System ID</div>
            <div className="admin-detail-value">#{request._id || request.id}</div>
          </div>
          
          <div className="admin-detail-row">
            <div className="admin-detail-label">Company</div>
            <div className="admin-detail-value">
              {request.company ? (
                <Link to={`/admin/organizations/${request.company._id || request.company.id}`}>
                  {request.company.name}
                </Link>
              ) : 'N/A'}
            </div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Currency</div>
            <div className="admin-detail-value">{requestCurrency}</div>
          </div>
          
          <div className="admin-detail-row">
            <div className="admin-detail-label">Amount</div>
            <div className="admin-detail-value">{formatCurrency(request.amount, requestCurrency)}</div>
          </div>
          
          <div className="admin-detail-row">
            <div className="admin-detail-label">Rate Types</div>
            <div className="admin-detail-value">
              {request.rateTypes && request.rateTypes.length > 0 
                ? request.rateTypes.map(type => 
                    type === 'floating' ? 'Floating' : 'Fixed'
                  ).join(', ')
                : (request.rateType === 'floating' ? 'Floating' : 'Fixed')
              }
            </div>
          </div>
          
          <div className="admin-detail-row">
            <div className="admin-detail-label">Duration</div>
            <div className="admin-detail-value">
              {request.duration === 'overnight' ? 'Overnight' : `${request.duration} months`}
            </div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Withdrawal Type</div>
            <div className="admin-detail-value">
              {request.withdrawalType === 'demand' ? 'Demand' :
              request.withdrawalType === 'early_allowed' ? 'Early Withdrawal Allowed' :
              request.withdrawalType === 'no_withdrawal' ? 'No Withdrawal Allowed' : 'Not specified'}
            </div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Target Banks</div>
            <div className="admin-detail-value">
              {request.targetBanks === 'all' ? 'All Banks' :
              request.targetBanks === 'affiliated' ? 'Affiliated Banks Only' :
              request.targetBanks === 'third_party' ? 'Third Party Banks Only' : 
              request.targetBanks || 'All Banks'}
            </div>
          </div>
          
          <div className="admin-detail-row">
            <div className="admin-detail-label">Bid Deadline</div>
            <div className="admin-detail-value">
              {formatDeadline(request.bidDeadline)}
            </div>
          </div>
          
          <div className="admin-detail-row">
            <div className="admin-detail-label">Created Date</div>
            <div className="admin-detail-value">{formatDate(request.createdAt)}</div>
          </div>
          
          {request.notes && (
            <div className="admin-detail-row">
              <div className="admin-detail-label">Notes</div>
              <div className="admin-detail-value">{request.notes}</div>
            </div>
          )}
        </div>
      </div>
      
      {/* Summary for completed requests */}
      {(request.status === 'completed' || request.status === 'pending_transfer') && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Request Summary</h3>
          </div>
          <div className="admin-card-body">
            <div className="admin-summary-box">
              <div className="admin-summary-row">
                <div className="admin-summary-label">Requested Amount:</div>
                <div className="admin-summary-value">{formatCurrency(request.amount, requestCurrency)}</div>
              </div>
              
              <div className="admin-summary-row">
                <div className="admin-summary-label">Total Allocated:</div>
                <div className="admin-summary-value">{formatCurrency(totalAllocated, requestCurrency)}</div>
              </div>
              
              <div className="admin-summary-row">
                <div className="admin-summary-label">Winning Bids:</div>
                <div className="admin-summary-value">{winningBids.length}</div>
              </div>
              
              <div className="admin-summary-row">
                <div className="admin-summary-label">Status:</div>
                <div className="admin-summary-value">
                  <span className={`status status-${request.status}`}>
                    {request.status.replace('_', ' ')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* All Bids section */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Bids ({bids.length})</h3>
        </div>
        <div className="admin-card-body">
          {bids.length === 0 ? (
            <div className="admin-no-data-message">No bids have been submitted yet.</div>
          ) : (
            <div className="admin-table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Bid ID</th>
                    <th>Bank</th>
                    <th>Amount</th>
                    <th>Interest Rate</th>
                    <th>Submission Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {bids.map(bid => (
                    <tr key={bid.id || bid._id}>
                      <td>#{bid.id || bid._id}</td>
                      <td>{bid.bank?.name || 'N/A'}</td>
                      <td>{formatCurrency(bid.amount, requestCurrency)}</td>
                      <td>{bid.interestRate}%</td>
                      <td>{formatDate(bid.createdAt)}</td>
                      <td>
                        <span className={`status status-${bid.status === 'approved' ? 'approved' : bid.status === 'rejected' ? 'rejected' : bid.status === 'cancelled' ? 'cancelled' : 'pending'}`}>
                          {bid.status || 'pending'}
                        </span>
                      </td>
                      <td>
                        <Link to={`/admin/bids/${bid.id || bid._id}`} className="admin-view-btn">
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {/* Winning Bids section for completed requests */}
      {(request.status === 'completed' || request.status === 'pending_transfer') && winningBids.length > 0 && (
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Winning Bids</h3>
        </div>
        <div className="admin-card-body">
          <div className="admin-table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Bid ID</th>
                  <th>Bank</th>
                  <th>Amount</th>
                  <th>Rate Type</th>
                  <th>Interest Rate</th>
                  <th>Allocated Amount</th>
                </tr>
              </thead>
              <tbody>
                {winningBids.map(bid => (
                  <tr key={`winning-${bid.id || bid._id}`}>
                    <td>
                      <Link to={`/admin/bids/${bid.id || bid._id}`}>
                        #{bid.id || bid._id}
                      </Link>
                    </td>
                    <td>
                      <Link to={`/admin/organizations/${bid.bank?._id || bid.bank?.id}`}>
                        {bid.bank?.name || 'N/A'}
                      </Link>
                    </td>
                    <td>{formatCurrency(bid.amount, requestCurrency)}</td>
                    <td>
                      <span >
                        {bid.rateType === 'floating' ? 'Floating' : 'Fixed'}
                      </span>
                    </td>
                    <td>
                      <span>
                        {bid.rateType === 'floating' 
                          ? `${bid.baseRate ? bid.baseRate.replace('_', ' ').toUpperCase() + ' + ' : ''}${bid.interestRate || 0}%`
                          : `${bid.interestRate || 0}%`
                        }
                      </span>
                    </td>
                    <td>{formatCurrency(bid.allocatedAmount, requestCurrency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )}
      
      {/* Allocated Funds section */}
      {(allocations && allocations.length > 0) && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Allocated Funds</h3>
          </div>
          <div className="admin-card-body">
            <div className="admin-table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Allocation ID</th>
                    <th>Bank</th>
                    <th>Amount</th>
                    <th>Interest Rate</th>
                    <th>Transfer Status</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                  </tr>
                </thead>
                <tbody>
                  {allocations.map(allocation => (
                    <tr key={allocation.id || allocation._id}>
                      <td>#{allocation.id || allocation._id}</td>
                      <td>{allocation.bank?.name || 'N/A'}</td>
                      <td>{formatCurrency(allocation.amount, requestCurrency)}</td>
                      <td>{allocation.interestRate}%</td>
                      <td>
                        <span className={`status status-${allocation.transferStatus === 'completed' ? 'approved' : 'pending'}`}>
                          {allocation.transferStatus || 'pending'}
                        </span>
                      </td>
                      <td>{allocation.startDate ? formatDate(allocation.startDate) : 'N/A'}</td>
                      <td>{allocation.endDate ? formatDate(allocation.endDate) : 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {/* Transactions section for completed requests */}
      {request.status === 'completed' && transactions.length > 0 && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Transactions</h3>
          </div>
          <div className="admin-card-body">
            <div className="admin-table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Transaction ID</th>
                    <th>Type</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Amount</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(transaction => (
                    <tr key={transaction.id || transaction._id}>
                      <td>#{transaction.id || transaction._id}</td>
                      <td>{transaction.type === 'allocation' ? 'Allocation' : 
                           transaction.type === 'return' ? 'Return' : 
                           transaction.type === 'interest_payment' ? 'Interest Payment' : 
                           transaction.type}</td>
                      <td>{transaction.fromOrganization?.name || 'N/A'}</td>
                      <td>{transaction.toOrganization?.name || 'N/A'}</td>
                      <td>{formatCurrency(transaction.amount, requestCurrency)}</td>
                      <td>{formatDate(transaction.transactionDate || transaction.createdAt)}</td>
                      <td>
                        <span className={`status status-${transaction.status === 'completed' ? 'approved' : transaction.status === 'failed' ? 'rejected' : 'pending'}`}>
                          {transaction.status || 'pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {/* Status Actions - only show certain buttons depending on current status */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Actions</h3>
        </div>
        <div className="admin-card-body">
          <div className="admin-actions">
            {request.status === 'bidding' && (
              <button
                className="btn btn-primary"
                onClick={() => handleStatusUpdate('pending_allocation')}
                disabled={actionInProgress}
              >
                {actionInProgress ? 'Processing...' : 'Close Bidding'}
              </button>
            )}
            
            {request.status === 'pending_allocation' && (
              <button
                className="btn btn-primary"
                onClick={() => handleStatusUpdate('pending_transfer')}
                disabled={actionInProgress}
              >
                {actionInProgress ? 'Processing...' : 'Approve Allocations'}
              </button>
            )}
            
            {request.status === 'pending_transfer' && (
              <button
                className="btn btn-primary"
                onClick={() => handleStatusUpdate('completed')}
                disabled={actionInProgress}
              >
                {actionInProgress ? 'Processing...' : 'Complete Request'}
              </button>
            )}
            
            {(request.status === 'bidding' || request.status === 'pending_allocation') && (
              <button
                className="btn btn-danger"
                onClick={() => handleStatusUpdate('cancelled')}
                disabled={actionInProgress}
              >
                {actionInProgress ? 'Processing...' : 'Cancel Request'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminRequestDetails;